import { useEffect, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import "../announcements/announcement.css";

// ---- branded loading experience ----
// Every visit fetches live data fresh (no cache), which takes a few
// seconds, so make the wait feel intentional: an ECG trace draws
// itself, steps reveal one by one, and a progress bar eases forward.
// Per-step durations in seconds. The earlier steps each get more
// breathing room so each label has time to register; the final
// "Polishing" step is intentionally brief so we don't sit on it for
// long if the data fetch outruns the animation. Cumulative ~15s,
// which lines up with a typical Cognito + FX + case-studies fetch.
const LOAD_STEPS = [
  { label: "Connecting to the system", durationS: 2.0 },
  { label: "Retrieving your proposal", durationS: 2.3 },
  { label: "Calculating costs & exchange rates", durationS: 2.5 },
  { label: "Compiling relevant case studies", durationS: 2.5 },
  { label: "Mapping project timelines", durationS: 2.0 },
  { label: "Loading public-holiday calendars", durationS: 1.8 },
  { label: "Building your proposal view", durationS: 2.0 },
  { label: "Polishing the final details", durationS: 0.6 },
];
// Cumulative time at which each step becomes "current".
const STEP_THRESHOLDS = LOAD_STEPS.reduce((acc, s, i) => {
  acc.push((acc[i - 1] ?? 0) + s.durationS);
  return acc;
}, []);

// Single sharp QRS beat, centred, straight lines only, normalised via
// pathLength so the dash sweep stays in sync.
const ECG_PATH = "M0,44 H148 L156,50 L168,8 L180,70 L188,36 L194,44 H360";

export function ProposalLoadingScreen({ announcement = null, ready = false, onContinue }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - t0) / 1000), 150);
    return () => clearInterval(id);
  }, []);
  const step = (() => {
    // The "current" step is the first threshold the elapsed time
    // hasn't crossed yet. Once we're past the last threshold, stay on
    // the final step so it acts as the lingering placeholder while the
    // data finishes loading.
    for (let i = 0; i < STEP_THRESHOLDS.length; i++) {
      if (elapsed < STEP_THRESHOLDS[i]) return i;
    }
    return LOAD_STEPS.length - 1;
  })();
  return (
    <div
      className="min-h-screen bg-[#0B1220] text-stone-100 flex items-center justify-center font-sans p-6"
      data-testid="proposal-loading"
      data-announcement={announcement ? "shown" : "none"}
    >
      <style>{`
        @keyframes ecg-slide {
          from { transform: translateX(0); }
          to   { transform: translateX(360px); }
        }
        @keyframes step-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className={`w-full text-center ${announcement ? "max-w-3xl" : "max-w-sm"}`}>
        <img
          src="/brand/medicys-logo.png"
          alt="Medicys"
          className="h-20 mx-auto mb-3 opacity-95 w-auto"
        />
        {/* ECG monitor, the reveal edge moves at constant HORIZONTAL
            speed (sliding gradient mask), so the spike snaps up/down
            like a real monitor, with a fading afterglow trail.
            Hidden while an announcement is showing. */}
        {!announcement && (
        <svg
          viewBox="0 0 360 80"
          className="w-80 h-20 mx-auto"
          aria-hidden
        >
          <defs>
            <pattern
              id="ecgGrid"
              width="18"
              height="18"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M18 0H0V18"
                fill="none"
                stroke="#3EA8DC"
                strokeOpacity="0.09"
                strokeWidth="0.5"
              />
            </pattern>
            <linearGradient
              id="ecgTrail"
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2="360"
              y2="0"
              spreadMethod="repeat"
            >
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset="0.55" stopColor="#fff" stopOpacity="0.12" />
              <stop offset="0.85" stopColor="#fff" stopOpacity="0.5" />
              <stop offset="0.99" stopColor="#fff" stopOpacity="1" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <mask
              id="ecgMask"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="360"
              height="80"
            >
              <rect
                x="-720"
                y="0"
                width="1440"
                height="80"
                fill="url(#ecgTrail)"
                style={{ animation: "ecg-slide 1.1s linear infinite" }}
              />
            </mask>
          </defs>
          <rect width="360" height="80" rx="4" fill="url(#ecgGrid)" />
          {/* phosphor ghost of the full trace */}
          <path
            d={ECG_PATH}
            fill="none"
            stroke="#3EA8DC"
            strokeOpacity="0.07"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <g mask="url(#ecgMask)">
            <path
              d={ECG_PATH}
              fill="none"
              stroke="#3EA8DC"
              strokeWidth="2.5"
              strokeLinejoin="round"
              style={{
                filter: "drop-shadow(0 0 6px rgba(62,168,220,0.9))",
              }}
            />
          </g>
        </svg>
        )}

        {announcement ? (
          <AnnouncementCard announcement={announcement} ready={ready} onContinue={onContinue} />
        ) : (
          <>
        {/* staggered step reveal */}
        <ul
          className="mt-8 text-left space-y-2.5 inline-block min-h-[240px] align-top"
          data-testid="loading-steps"
        >
          {LOAD_STEPS.map((entry, i) => {
            if (i > step) return null;
            const done = i < step;
            const label = entry.label;
            return (
              <li
                key={label}
                className="flex items-center gap-2.5 text-sm"
                style={{ animation: "step-in 0.45s ease-out both" }}
                data-testid={`loading-step-${i}`}
              >
                <span
                  className={`w-4 h-4 rounded-full shrink-0 inline-flex items-center justify-center ${
                    done ? "bg-[#3EA8DC]" : "border border-[#3EA8DC]/60"
                  }`}
                >
                  {done ? (
                    <Check
                      className="w-2.5 h-2.5 text-[#0B1220]"
                      strokeWidth={3}
                    />
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3EA8DC] animate-pulse" />
                  )}
                </span>
                <span className={done ? "text-stone-500" : "text-stone-200"}>
                  {label}
                  {!done && <span className="animate-pulse">…</span>}
                </span>
              </li>
            );
          })}
        </ul>

        <p className="mt-8 text-[11px] font-mono tracking-wide text-stone-500">
          Fetching your latest live pricing. This takes a few seconds.
        </p>
          </>
        )}
      </div>
    </div>
  );
}

// Announcement splash: the client must press Continue before the
// proposal is shown, however quickly it loads behind the scenes.
export function AnnouncementCard({ announcement, ready, onContinue }) {
  return (
    <div
      className="mt-4 text-left rounded-2xl border border-[#3EA8DC]/30 bg-white/[0.04] backdrop-blur-md p-6 sm:p-10"
      style={{ animation: "step-in 0.5s ease-out both" }}
      data-testid="announcement-card"
    >
      <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#3EA8DC]">
        A note from Medicys
      </p>
      <h2 className="mt-3 text-xl sm:text-2xl font-semibold text-white leading-snug" data-testid="announcement-title">
        {announcement.title}
      </h2>
      {announcement.body_html ? (
        <div
          className="ann-rich ann-rich--dark mt-3 text-sm sm:text-base leading-relaxed"
          data-testid="announcement-body"
          dangerouslySetInnerHTML={{ __html: announcement.body_html }}
        />
      ) : (
        <p className="mt-3 text-sm sm:text-base text-stone-300 leading-relaxed whitespace-pre-line" data-testid="announcement-body">
          {announcement.body}
        </p>
      )}
      <button
        type="button"
        onClick={onContinue}
        className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#3EA8DC] px-6 py-3 text-sm font-semibold text-[#0B1220] transition-[background-color,transform] hover:bg-[#5fbbe6] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        data-testid="announcement-continue"
      >
        Continue to your proposal
        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </button>
      <p className="mt-4 text-[11px] font-mono tracking-wide text-stone-500" data-testid="announcement-status">
        {ready ? "Your proposal is ready." : (
          <>
            Your proposal is being prepared in the background
            <span className="animate-pulse">…</span>
          </>
        )}
      </p>
    </div>
  );
}
